import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

import {
  Component,
  DOCUMENT,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewRef,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Title } from '@angular/platform-browser';
import {
  IpoStringService,
  IpoTranslatePipe,
  LoadingState,
} from '@ip-only/front-end-library';
import {
  Circle,
  CircleMarker,
  Icon,
  Layer,
  Map,
  Marker,
  TileLayer,
} from 'leaflet';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { RolloutProgressReportEntities } from '../../shared/self-inspection-reports/service/entities';
import { InspectionReportInitService } from '../../shared/self-inspection-reports/service/initial-values/inspection-report-init.service';
import { RolloutProgressReportsService } from '../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { CombinedLoadingState } from '../../shared/util/combineLoadingStates';
import { isTopLevelDomainFI } from '../../shared/util/deploy-helper';
import { getFetchErrorFromLoadingState } from '../../shared/util/error-message-helper';
import { ResourcesService } from '../../user/resources.service';
import { Resource } from '../../user/user';
import { PingApiService } from '../service/api/ping-api.service';
import { DefinitionsService } from '../service/definitions/definitions.service';
import { MapService } from '../service/map/map.service';
import { PermitAreaService } from '../service/map/permit/permit-area.service';
import { PointService } from '../service/map/point/point.service';
import { SelectSpanModeService } from '../service/map/span/select-span-mode/select-span-mode.service';
import { OfflineError } from '../service/offline-check.interceptor';
import {
  LeadMarkerSelection,
  PointLayer,
  PointLayerTypes,
  PopupTypes,
} from '../span-progress-entities';
import { findLinkedKp } from '../utils/report-row.helper';
import { MapInteractionService } from './map-interaction/map-interaction.service';
import { SidePanel } from './map-controls/map-controls.component';
import { DuctInteractionService } from './duct-table/services/duct-interaction.service';
import { pointInfo } from './map-view-constants';
import { ReportModalService } from '../span-progress-report/report-modal.service';

export const menuAnimationTime = 300;
const isMobileScreen = window.matchMedia(`(max-width: 768px)`).matches;

const animations = [
  trigger('menuInOut', [
    transition(':enter', [
      style({ width: 0 }),
      animate(
        `${menuAnimationTime}ms ease-in-out`,
        style({ width: isMobileScreen ? '260px' : '400px' }),
      ),
    ]),
    transition(':leave', [
      animate(`${menuAnimationTime}ms ease-in-out`, style({ width: '0px' })),
    ]),
  ]),
  trigger('mapResize', [
    state('resize', style({ right: isMobileScreen ? '260px' : '400px' })),
    state('full', style({ right: '0px' })),
    transition('* <=> *', [animate(`${menuAnimationTime}ms ease-in-out`)]),
  ]),
];
@Component({
  selector: 'app-span-progress-view',
  templateUrl: './span-progress-view.component.html',
  styleUrls: ['./span-progress-view.component.scss'],
  animations: animations,
  standalone: false,
})
export class SpanProgressViewComponent implements OnInit, OnDestroy {
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  private readonly reportsService = inject(RolloutProgressReportsService);
  private readonly initService = inject(InspectionReportInitService);
  private readonly mapInteractionService = inject(MapInteractionService);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly reportModalService = inject(ReportModalService);
  private readonly ductInteractionService = inject(DuctInteractionService);
  private readonly route = inject(ActivatedRoute);

  modalState$ = this.reportModalService.getModalState$();
  selectedResource$: Observable<Resource | null>;
  loadingState$: Observable<CombinedLoadingState>;
  cancelSub$ = new Subject<void>();
  private readonly mapReady$ = new BehaviorSubject(false);
  private selectedLeadKey: string | null = null;
  offlineStatus$: Observable<OfflineError | boolean> =
    this.pingApiService.pingApiStatus();

  mapHeight: number;
  showLocationModal = false;
  menuViewRef: ViewRef;
  baseMaps: TileLayer[] = [];

  map: Map;
  gpsMarker: Circle | null = null;
  gpsDotMarker: Marker | null = null;
  geolocationImage = new Icon({
    iconUrl: './assets/leaflet/images/geolocation-point.svg',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    shadowUrl: './assets/leaflet/images/geolocation-point-shadow.png',
    shadowSize: [32, 32],
    shadowAnchor: [16, 16],
    className: 'geolocation',
  });

  activeSidePanel: SidePanel | null = null;
  isDeliveryPortalFI: boolean = isTopLevelDomainFI();

  isSelectSpanMode = this.selectSpanModeService.isSelectSpanMode;

  constructor(
    private readonly mapService: MapService,
    private readonly pointService: PointService,
    private readonly resourcesService: ResourcesService,
    private readonly stringService: IpoStringService,
    private readonly pingApiService: PingApiService,
    private readonly permitAreaService: PermitAreaService,
    private readonly titleService: Title,
    private readonly translate: IpoTranslatePipe,
    private readonly definitionsService: DefinitionsService,
  ) {
    this.titleService.setTitle(
      this.translate.transform(this.isDeliveryPortalFI ? 'tabFI' : 'tabSE'),
    );

    this.handleIsMultiSpanEffect();
  }

  ngOnInit() {
    this.definitionsService.fetchDefinitions();

    this.loadingState$ = this.mapService.getLoadingState$();
    this.selectedResource$ = this.resourcesService.getSelectedResource$();
    this.selectedResource$
      .pipe(
        takeUntil(this.cancelSub$),
        distinctUntilChanged((prev, curr) => prev?.value === curr?.value),
      )
      .subscribe((resource) => {
        if (resource) {
          this.mapReady$.next(false);
          this.getReports(resource.value);
          this.mapService.initializeMap(resource.value);
        }
      });
    this.selectLeadFromQueryParams();
  }

  ngOnDestroy() {
    this.cancelSub$.next();
  }

  getVersion() {
    return this.mapService.getVersion();
  }

  getMapBounds() {
    return this.mapService.getMapBounds();
  }

  getLayers(): Layer[] {
    return this.mapService.getLayers();
  }

  getOverlayLayers() {
    const permitLayer = this.permitAreaService.getLayerData();
    const pointLayerData = this.pointService.getLayerData();
    return Object.keys(pointLayerData).reduce((accumulatedOverlays, key) => {
      return {
        ...accumulatedOverlays,
        [this.getLayerText(key as PointLayerTypes)]: pointLayerData[key],
        [this.stringService.getText('legend.permitArea')]: permitLayer,
      };
    }, {});
  }

  getReports = (ifs: string): void => {
    if (this.resourcesService.getStoredResource()) {
      this.initService.setReportUrlInitValue(
        RolloutProgressReportEntities.v3.inspectionReportUrlTypes.spanProgress
          .report,
      );

      this.initService.setIfsInitValue(ifs);

      this.reportsService
        .getV3ReportsByIfs$(ifs)
        .pipe(takeUntil(this.cancelSub$));
    }
  };

  getFetchErrors(loadingStateErrors: CombinedLoadingState['errors']) {
    return this.stringService.getText(
      getFetchErrorFromLoadingState({
        error: loadingStateErrors[0],
      } as LoadingState),
    );
  }

  onGeolocationDenied() {
    this.showLocationModal = true;
  }

  closeLocationModal() {
    this.showLocationModal = false;
  }

  onMapReady() {
    this.mapReady$.next(true);
  }

  setActiveSidePanel(panel: SidePanel) {
    if (this.activeSidePanel === 'reports' && panel === 'ducts') {
      this.ductInteractionService.setSearchValue('');
    }

    this.activeSidePanel = this.activeSidePanel === panel ? null : panel;
  }

  disablePointClicks = () => {
    Object.keys(this.pointService.getLayerData()).forEach((key) => {
      this.pointService.getLayerData()[key].off('click');
    });
  };

  enablePointClicks = () => {
    Object.keys(this.pointService.getLayerData()).forEach((key) => {
      this.pointService.getLayerData()[key].on('click', (e) => {
        const popupType = pointInfo[key as PointLayerTypes].popup;
        if (popupType) this.pointService.resetOnClick(e, popupType);
      });
    });
  };

  onPanelSelected(panel: SidePanel): void {
    this.activeSidePanel = this.activeSidePanel === panel ? null : panel;
  }

  onSidePanelClose(): void {
    this.activeSidePanel = null;
  }

  private getLayerText(typeId: PointLayerTypes) {
    return this.stringService.getText('legend.' + typeId.toLowerCase());
  }
  private readonly setMapToDarkMode = () => {
    const canvas = this.document.querySelector('.leaflet-map-pane canvas');
    this.renderer.setStyle(canvas, 'background-color', 'rgba(35, 35, 54, 0.5)');
  };

  private readonly setMapToNormalMode = () => {
    const canvas = this.document.querySelector('.leaflet-map-pane canvas');

    if (canvas) {
      this.renderer.removeStyle(canvas, 'background-color');
    }
  };

  private selectLeadFromQueryParams(): void {
    combineLatest([
      this.route.paramMap,
      this.route.queryParamMap,
      this.pointService.getLoadingState$(),
      this.selectedResource$,
      this.mapReady$,
    ])
      .pipe(
        takeUntil(this.cancelSub$),
        filter((selectionState) => this.isReadyToSelectLead(selectionState)),
      )
      .subscribe(([params, query]) => this.selectLead(params, query));
  }

  private isReadyToSelectLead([params, , loading, resource, mapReady]: [
    ParamMap,
    ParamMap,
    LoadingState,
    Resource | null,
    boolean,
  ]): boolean {
    return (
      loading.hasLoaded &&
      !loading.isLoading &&
      resource?.value === params.get('ifs') &&
      mapReady
    );
  }

  private selectLead(params: ParamMap, query: ParamMap): void {
    const leadId = query.get('leadId');
    if (!leadId) {
      this.selectedLeadKey = null;
      return;
    }

    const selectionKey = `${params.get('ifs')}:${leadId}`;
    if (
      selectionKey !== this.selectedLeadKey &&
      this.selectLeadMarker(leadId)
    ) {
      this.selectedLeadKey = selectionKey;
    }
  }

  private selectLeadMarker(leadId: string): boolean {
    const selection = this.getLeadMarkerSelection(leadId);

    if (selection?.linkedKp) {
      this.mapInteractionService.selectPointFromReportPanel(
        selection.layer as CircleMarker,
        PopupTypes.Ductpoint,
        selection.linkedKp.id,
      );
      return true;
    }

    if (selection) {
      this.mapInteractionService.onSelectSearchedItem(
        selection.layer as CircleMarker,
      );
      return true;
    }

    return false;
  }

  private getLeadMarkerSelection(
    leadId: string,
  ): LeadMarkerSelection | undefined {
    const layerData = this.pointService.getLayerData();

    return (
      this.getDuctpointSelection(layerData, leadId) ??
      this.getHpMarkerSelection(layerData, leadId)
    );
  }

  private getDuctpointSelection(
    layerData: ReturnType<PointService['getLayerData']>,
    leadId: string,
  ): LeadMarkerSelection | undefined {
    return ['KP', 'SDP']
      .flatMap((type) =>
        layerData[type] ? (layerData[type].getLayers() as PointLayer[]) : [],
      )
      .map((layer) => ({
        layer,
        linkedKp: findLinkedKp(layer.feature?.properties, leadId, true),
      }))
      .find((selection) => selection.linkedKp !== undefined);
  }

  private getHpMarkerSelection(
    layerData: ReturnType<PointService['getLayerData']>,
    leadId: string,
  ): LeadMarkerSelection | undefined {
    const hpLayer = (
      layerData['HP']?.getLayers() as PointLayer[] | undefined
    )?.find((layer) => layer.feature?.properties?.sourceKey === leadId);

    return hpLayer ? { layer: hpLayer } : undefined;
  }

  private handleIsMultiSpanEffect() {
    effect(() => {
      if (this.isSelectSpanMode()) {
        this.disablePointClicks();
        this.setMapToDarkMode();
        return;
      } else {
        this.mapInteractionService.resetMultiSpan();
        this.setMapToNormalMode();
      }
      this.enablePointClicks();
    });
  }
}
